import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GenericTag } from '@/styles/GenericTag';
import { Trash2, Plus, Copy, Download, Upload } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { useEntityList } from '@/hooks/useEntityList';
import { useCompanyLookups } from '@/hooks/useCompanyLookups';
import { exportEntityCsv } from '@/lib/csv';
import { InvoiceService } from '../services/invoiceService';
import { Invoice } from '../types';
import '../Invoices.css';
import { trackInvoicePaymentReceived, trackInvoicePaymentUpdated, trackButtonClick, trackFormSubmission } from '@/lib/posthog-events';
import { getApiUrl } from '@/lib/api-config';
import { GenericTable, GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { InvoicePaymentForm } from './InvoicePaymentForm';
import { fetchEntityAttachments } from '@/lib/entity-attachments';
import { uploadInvoicePaymentAttachments } from '../services/invoicePaymentAttachments';
import { postInvoicePayment } from '../services/invoicePaymentsService';


export function PaymentsTable() {
  const { companyId, user } = useUser();
  const [showForm, setShowForm] = React.useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  // The list: ONE server-driven query (search + sort + page) -- the database searches, sorts and pages every payment the
  // user may see (also by the invoice it pays); the counters come with each row (hooks/useEntityList.ts).
  const list = useEntityList<any, any>({
    entityType: 'invoice_payment',
    enabled: !!companyId,
    mode: 'pages',
    includeCounts: true,
    defaultSort: { columnKey: 'payment_id', direction: 'asc', sortKey: 'created_at' },
    mapRow: (row) => row,
  });
  const payments = list.items;
  const { projects } = useCompanyLookups(!!companyId, { includeInactiveProjects: true });
  // The invoices of the "Invoice" dropdown of the form (loaded when the form opens), not of the table.
  const [invoices, setInvoices] = React.useState<Invoice[]>([]);
  const [currentPayment, setCurrentPayment] = React.useState<any>(null);
  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);

  type PaymentSavePayload = {
    paymentData: any;
    newAttachments: Array<{ file: File; name: string; size: number; type: string }>;
    removedAttachmentIds: string[];
  };

  const formatStatusLabel = (status: string) =>
    status.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

  const loadInvoices = React.useCallback(async () => {
    if (!companyId) return [];
    try {
      const invoicesData = await InvoiceService.getInvoices();
      setInvoices(invoicesData);
      return invoicesData;
    } catch (error) {
      console.error('Error loading invoices:', error);
      return [];
    }
  }, [companyId]);

  const handleNewPayment = async () => {
    if (!companyId || !user) return;
    
    try {
      await loadInvoices();
      // Don't auto-generate payment_number - it's user input
      setCurrentPayment({
        id: '',
        company_id: companyId,
        project_id: '',
        invoice_id: '',
        payment_number: '', // User input - leave empty
        client_name: '',
        status: 'pending',
        payment_date: new Date().toISOString().split('T')[0],
        amount: 0,
        notes: '',
        created_by: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: null
      });
      setShowForm(true);
    } catch (error) {
      console.error('Error creating new payment:', error);
    }
  };

  const handleEditPayment = async (payment: any) => {
    try {
      await loadInvoices();
      const attachmentData = await fetchEntityAttachments('invoice_payment', payment.id);

      setCurrentPayment({
        ...payment,
        attachments: attachmentData
      });
      setShowForm(true);
    } catch (error) {
      console.error('Error loading payment:', error);
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;

    try {
      // Deleting the payment cascades to its attachments (DB rows +
      // Storage files) generically on the backend -- see
      // core/attachment-cascade.js (entity type invoice_payment). No separate attachment cleanup needed here.
      const response = await fetch(getApiUrl(`api/entities/invoice_payment/${paymentId}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          company_id: companyId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete payment');
      }

      list.reload();
    } catch (error) {
      console.error('Error deleting payment:', error);
    }
  };

  const handleSavePayment = async ({ paymentData, newAttachments, removedAttachmentIds }: PaymentSavePayload) => {
    if (!companyId || !user) return;
    
    try {
      let savedPayment;
      if (currentPayment?.id) {
        // Update existing payment via generic API
        const response = await fetch(getApiUrl(`api/entities/invoice_payment/${currentPayment.id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.id,
            company_id: companyId,
            ...paymentData,
            updated_at: new Date().toISOString()
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update payment');
        }

        const result = await response.json();
        savedPayment = result.data;
      } else {
        // Create new payment (the same request the invoice's "Create Payment" sends)
        savedPayment = await postInvoicePayment({ ...paymentData }, { companyId, userId: user.id });
      }
      
      // In edit mode, InvoicePaymentForm already uploaded any new files
      // immediately (a real payment.id already existed), so `newAttachments`
      // arrives here empty and this is a no-op. In create mode, there was no
      // payment.id yet when the files were picked, so InvoicePaymentForm
      // only staged them locally -- upload them now that savedPayment.id
      // exists, same "create record, then attach files to the new id"
      // pattern as Create Lead.
      if (newAttachments && newAttachments.length > 0 && savedPayment?.id) {
        try {
          await uploadInvoicePaymentAttachments(
            savedPayment.id,
            newAttachments.map((a) => a.file),
            companyId,
            user.id
          );
        } catch (uploadError) {
          console.error('Error uploading attachments after create:', uploadError);
        }
      }

      list.reload();
      
      // Track successful payment creation/update with PostHog
      if (currentPayment?.id && currentPayment.id !== '') {
        trackInvoicePaymentUpdated({
          payment_id: savedPayment.id,
          payment_number: paymentData.payment_number,
          project_id: paymentData.project_id,
          invoice_id: paymentData.invoice_id,
          amount: paymentData.amount,
          status: paymentData.status,
          payment_date: paymentData.payment_date,
          attachment_count: newAttachments?.length || 0,
          company_id: companyId,
          updated_by: user.id
        });
      } else {
        trackInvoicePaymentReceived({
          payment_id: savedPayment.id,
          payment_number: paymentData.payment_number,
          project_id: paymentData.project_id,
          invoice_id: paymentData.invoice_id,
          amount: paymentData.amount,
          status: paymentData.status,
          payment_date: paymentData.payment_date,
          attachment_count: newAttachments?.length || 0,
          company_id: companyId,
          created_by: user.id
        });
      }

      // Track successful form submission
      trackFormSubmission({
        form_type: (currentPayment?.id && currentPayment.id !== '') ? 'invoice_payment_update' : 'invoice_payment_creation',
        success: true
      });
      
      return savedPayment;
    } catch (error: any) {
      console.error('Error saving payment:', error);
      
      // Track failed payment save with PostHog
      trackFormSubmission({
        form_type: (currentPayment?.id && currentPayment.id !== '') ? 'invoice_payment_update' : 'invoice_payment_creation',
        success: false,
        error_message: error.message || 'Failed to save payment'
      });
      
      throw error;
    }
  };


  // Handle copy selected payments
  const handleCopySelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one payment to copy.');
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to copy payments');
      return;
    }

    try {
      const selectedPays = payments.filter(pay => selectedRows.includes(pay.id));
      let successCount = 0;
      let errorCount = 0;

      // Copy each selected payment - let API generate payment_id automatically
      for (const pay of selectedPays) {
        try {
          const response = await fetch(getApiUrl('api/entities/invoice_payment'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.id,
              company_id: companyId,
              // Don't include payment_id - let API generate it
              project_id: pay.project_id || null,
              invoice_id: pay.invoice_id || null,
              payment_number: pay.payment_number || null, // Keep user's payment number
              client_name: pay.client_name || null,
              status: 'pending', // Always set to pending for copies
              payment_date: pay.payment_date,
              amount: pay.amount || 0,
              notes: pay.notes || null,
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Error copying payment:', errorData);
            errorCount++;
            continue;
          }
          successCount++;
        } catch (error) {
          console.error('Error copying payment:', error);
          errorCount++;
        }
      }

      // Refresh the list
      list.reload();
      setSelectedRows([]);
      if (errorCount > 0) {
        alert(`Copied ${successCount} payment(s) successfully. ${errorCount} failed.`);
      } else {
        alert(`Successfully copied ${successCount} payment(s).`);
      }
    } catch (error) {
      console.error('Error copying payments:', error);
      alert('Error copying payments. Please try again.');
    }
  };

  // Handle delete selected payments
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one payment to delete.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} payment(s)? This action cannot be undone.`)) {
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to delete payments');
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const paymentId of selectedRows) {
        try {
          // Deleting the payment cascades to its attachments (DB rows +
          // Storage files) generically on the backend -- see
          // handleDeletePayment above.
          const response = await fetch(getApiUrl(`api/entities/invoice_payment/${paymentId}`), {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.id,
              company_id: companyId
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Error deleting payment:', errorData);
            errorCount++;
            continue;
          }
          successCount++;
        } catch (error) {
          console.error('Error deleting payment:', error);
          errorCount++;
        }
      }

      // Refresh the list
      list.reload();
      setSelectedRows([]);
      if (errorCount > 0) {
        alert(`Deleted ${successCount} payment(s) successfully. ${errorCount} failed.`);
      } else {
        alert(`Successfully deleted ${successCount} payment(s).`);
      }
    } catch (error) {
      console.error('Error deleting payments:', error);
      alert('Error deleting payments. Please try again.');
    }
  };

  // Export to CSV: the payments selected in the table, or -- with nothing selected -- EVERY payment of the current
  // search, read by the backend (not only the page that is showing).
  const handleExportSelected = () => exportEntityCsv(list.exportRows, selectedRows, 'payments', 'payments');

  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget) {
      if (hasUnsavedChanges) {
        if (window.confirm('Are you sure you want to lose unsaved progress?')) {
          setShowForm(false);
        }
      } else {
        setShowForm(false);
      }
    }
  }

  // Map payment statuses to GenericTag status types - use exact status names
  const mapPaymentStatusToTag = (status: string): 'pending' | 'posted' | 'voided' => {
    switch (status?.toLowerCase()) {
      case 'posted':
        return 'posted';
      case 'pending':
        return 'pending';
      case 'voided':
        return 'voided';
      default:
        return 'pending';
    }
  };

  // Column definitions for GenericTable
  const columns: GenericTableColumn<any>[] = [
    {
      key: 'payment_id',
      header: 'Payment ID',
      width: '6.5rem',
      sortable: true,
      sortKey: 'payment_id',
      render: (row) => <span className="font-medium">{row.payment_id || '-'}</span>,
    },
    {
      key: 'invoice_id',
      header: 'Invoice',
      width: '12rem',
      sortable: true,
      sortKey: 'invoice',
      render: (row) => `${row.invoice?.invoice_id || '-'} - ${row.invoice?.name || 'Untitled Invoice'}`,
    },
    {
      key: 'project_id',
      header: 'Project',
      width: '10rem',
      sortable: true,
      sortKey: 'project',
      render: (row) => projects.find((p) => p.id === row.project_id)?.name || 'Unknown Project',
    },
    {
      key: 'client_name',
      header: 'Client',
      width: '10rem',
      sortable: true,
      sortKey: 'client_name',
      render: (row) => row.client_name || 'N/A',
    },
    {
      key: 'status',
      header: 'Status',
      width: '7rem',
      sortable: true,
      sortKey: 'status',
      render: (row) => (
        <GenericTag 
          type="status" 
          value={mapPaymentStatusToTag(row.status || 'pending')}
        />
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      width: '7rem',
      align: 'right' as const,
      sortable: true,
      sortKey: 'amount',
      render: (row) => `$${Number(row.amount || 0).toFixed(2)}`,
    },
    {
      key: 'attachment_count',
      header: 'Attachments',
      width: '6.25rem',
      sortable: true,
      sortKey: 'attachment_count',
      render: (row) => (
        <span className="text-center block">{row.attachment_count || 0}</span>
      ),
    },
    {
      key: 'comment_count',
      header: 'Comments',
      width: '6.25rem',
      sortable: true,
      sortKey: 'comment_count',
      render: (row) => (
        <span className="text-center block">{row.comment_count || 0}</span>
      ),
    },
  ];

  // Menu items for ThreeDotMenuButton
  const menuItems = [
    {
      label: 'Copy',
      icon: <Copy className="w-4 h-4" />,
      onClick: handleCopySelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: () => {},
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2">
          <NewItemButton
            label="New"
            onClick={() => {
              trackButtonClick('new_invoice_payment_button', { context: 'cost_management' });
              handleNewPayment();
            }}
          />
          <SearchButton
            onSearch={list.setSearchInput}
            placeholder="Search"
          />
          <ThreeDotMenuButton items={menuItems} />
        </div>
      </div>

      {/* Generic Table */}
      <GenericTable
        columns={columns}
        {...list.tableProps}
        rowKey={(row) => row.id}
        scrollContainerMaxWidth="calc(100vw - 19.2rem)"
        minTableWidth={1200}
        onRowClick={handleEditPayment}
        selectable={true}
        selectedKeys={selectedRows}
        onSelectionChange={setSelectedRows}
      />

      {showForm && (
        <InvoicePaymentForm
              payment={currentPayment} 
              projects={projects}
              invoices={invoices}
          onClose={() => {
            setShowForm(false);
            setCurrentPayment(null);
            setHasUnsavedChanges(false);
          }}
              onSave={handleSavePayment}
          isOpen={showForm}
            />
      )}
    </div>
  );
}

